import React, { useEffect, useState } from 'react';
import styles from './HomeModern.module.scss';
import Button from '../../Buttons/Button/Button';
import { ButtonStyleType, TextSize } from '../../../types/button.types';
import { NavigateToInternalUrl } from '../../../utils/api.utils';
import { Routes } from '../../../types/constants';
import { HomeCollection, HomeHighlight, HomePromo } from '../../../types/home.types';
import { fetchHighlights } from '../../../services/highlight.service';
import { fetchPromos } from '../../../services/promo.service';
import { fetchCollections } from '../../../services/collection.service';
import { fetchHomeFightCards } from '../../../services/fight-cards.service';
import SectionHeader from './components/SectionHeader/SectionHeader';
import PromoCard from './components/PromoCard/PromoCard';
import HighlightCard from './components/HighlightCard/HighlightCard';
import CollectionCard from './components/CollectionCard/CollectionCard';
import ActionTile from './components/ActionTile/ActionTile';
import fightHeroMobile from '../../../assets/images/home/fight-punch-1300x865.webp';
import fightHeroDesktop from '../../../assets/images/home/fight-punch-1800x1198.webp';
import { HomeFightCard } from '../../../types/fight-cards.types';
import { shareCombatTv } from '../../../utils/share.utils';
import { toast } from 'react-toastify';
import { useSubscriptionStatus } from '../../../providers/subscription-status.provider';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { isAdmin } from '../../../utils/roles';
import { buildJoinUrl } from '../../../utils/join';

const fightHeroSrcSet = `${fightHeroMobile} 1300w, ${fightHeroDesktop} 1800w`;

const HomeModern = () => {
    const [promos, setPromos] = useState<HomePromo[]>([]);
    const [promoStatus, setPromoStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [highlights, setHighlights] = useState<HomeHighlight[]>([]);
    const [highlightStatus, setHighlightStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [collections, setCollections] = useState<HomeCollection[]>([]);
    const [collectionStatus, setCollectionStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [fightCards, setFightCards] = useState<HomeFightCard[]>([]);
    const [fightCardStatus, setFightCardStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const { hasActiveSubscription } = useSubscriptionStatus();
    const { getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const isAdminUser = isAdmin(userInfo?.roles);
    const shouldRenderPromoSection = promoStatus === 'success' && promos.length > 0;
    const shouldRenderHighlightSection = highlightStatus === 'success' && highlights.length > 0;
    const shouldRenderCollectionSection = collectionStatus === 'success' && collections.length > 0;
    const shouldRenderFightCardSection = fightCardStatus === 'success' && fightCards.length > 0;
    const shouldReservePromoSection = promoStatus === 'loading' || shouldRenderPromoSection;
    const shouldReserveHighlightSection = highlightStatus === 'loading' || shouldRenderHighlightSection;
    const shouldReserveCollectionSection = collectionStatus === 'loading' || shouldRenderCollectionSection;
    const shouldReserveFightCardSection = fightCardStatus === 'loading' || shouldRenderFightCardSection;
    const skeletonItems = [0, 1, 2];

    const formatEventDate = (isoDate: string) => {
        const parsedDate = new Date(isoDate);
        if (Number.isNaN(parsedDate.getTime())) {
            return '';
        }

        return new Intl.DateTimeFormat(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        }).format(parsedDate);
    };

    const handleShare = async () => {
        const result = await shareCombatTv({
            title: 'Combat TV',
            text: 'Invite friends to watch combat sports on Combat TV.'
        });

        if (result === 'copied') {
            toast.success('Link copied to clipboard.');
        }

        if (result === 'failed') {
            toast.error('Unable to share. Please copy the link.');
        }
    };

    const handleFightCardBrowse = (event: React.MouseEvent<HTMLAnchorElement>, eventId: string) => {
        event.preventDefault();
        NavigateToInternalUrl(`${Routes.FIGHT_CARD_EVENT}/${eventId}` as Routes);
    };

    useEffect(() => {
        let isMounted = true;
        setPromoStatus('loading');

        fetchPromos()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setPromos(data);
                setPromoStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Promo fetch failed', { error });
                setPromos([]);
                setPromoStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setCollectionStatus('loading');

        fetchCollections()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setCollections(data);
                setCollectionStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Collection fetch failed', { error });
                setCollections([]);
                setCollectionStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setHighlightStatus('loading');

        fetchHighlights()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setHighlights(data);
                setHighlightStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Highlight fetch failed', { error });
                setHighlights([]);
                setHighlightStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setFightCardStatus('loading');

        fetchHomeFightCards(3, userInfo?.token)
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                const items = (data.items ?? []).map((event) => ({
                    ...event,
                    dateLabel: formatEventDate(event.eventDate)
                }));
                setFightCards(items);
                setFightCardStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Fight cards fetch failed', { error });
                setFightCards([]);
                setFightCardStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, [isAdminUser, userInfo?.token]);

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <img
                    src={fightHeroDesktop}
                    srcSet={fightHeroSrcSet}
                    sizes="100vw"
                    alt="Combat TV fight action"
                    className={styles.heroImage}
                    decoding="async"
                />
                <div className={styles.heroOverlay} />
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Live + On Demand</span>
                    <h1 className={styles.heroTitle}>NZ’s premier fight destination — since 1995.</h1>
                    <p className={styles.heroSubtitle}>
                        Premium combat sports, available live and on demand. Watch the biggest events,
                        follow rising stars, and replay your favorite moments instantly.
                    </p>
                    <div className={styles.heroActions}>
                        {!hasActiveSubscription ? (
                            <Button
                                style={[styles.primaryButton, styles.heroButton].join(' ')}
                                styleType={ButtonStyleType.FILL}
                                textSize={TextSize.LARGE}
                                text="Subscribe Now"
                                callback={() => NavigateToInternalUrl(buildJoinUrl({
                                    offeringId: 'monthly',
                                    source: 'home_hero'
                                }))}
                            />
                        ) : null}
                        <Button
                            style={[styles.secondaryButton, styles.heroButton].join(' ')}
                            styleType={ButtonStyleType.OUTLINE}
                            textSize={TextSize.MEDIUM}
                            text="Browse Content"
                            callback={() => NavigateToInternalUrl(Routes.VIDEOS)}
                        />
                    </div>
                </div>
            </section>

            {shouldReservePromoSection ? (
                <section className={styles.section}>
                    <SectionHeader
                        title="Featured Promos"
                        subtitle="Upcoming events and must-see specials curated for you."
                    />
                    <div className={styles.promoGrid}>
                        {promoStatus === 'loading' ? (
                            skeletonItems.map((item) => (
                                <div key={item} className={styles.promoSkeleton} aria-hidden="true">
                                    <div className={styles.promoSkeletonMedia} />
                                    <div className={styles.promoSkeletonBody}>
                                        <div className={styles.promoSkeletonLine} />
                                        <div className={styles.promoSkeletonLineShort} />
                                    </div>
                                </div>
                            ))
                        ) : promos.map((promo) => <PromoCard key={promo.id} promo={promo} />)}
                    </div>
                </section>
            ) : null}

            {shouldReserveHighlightSection ? (
                <section className={styles.sectionAlt}>
                    <SectionHeader
                        title="Highlights to Watch"
                        subtitle="Recent fights and new content ready to stream."
                    />
                    <div className={styles.highlightRail} role="list">
                        {highlightStatus === 'loading' ? (
                            skeletonItems.map((item) => (
                                <div key={item} className={styles.highlightSkeleton} aria-hidden="true">
                                    <div className={styles.highlightSkeletonMedia} />
                                    <div className={styles.highlightSkeletonBody}>
                                        <div className={styles.highlightSkeletonLine} />
                                        <div className={styles.highlightSkeletonLine} />
                                    </div>
                                </div>
                            ))
                        ) : highlights.map((highlight) => <HighlightCard key={highlight.id} highlight={highlight} />)}
                    </div>
                </section>
            ) : null}

            {shouldReserveCollectionSection ? (
                <section className={styles.section}>
                    <SectionHeader
                        title="Explore Collections"
                        subtitle="Curated experiences for every fight fan."
                    />
                    <div className={styles.collectionGrid}>
                        {collectionStatus === 'loading' ? (
                            skeletonItems.map((item) => (
                                <div key={item} className={styles.collectionSkeleton} aria-hidden="true">
                                    <div className={styles.collectionSkeletonMedia} />
                                    <div className={styles.collectionSkeletonBody}>
                                        <div className={styles.collectionSkeletonLine} />
                                        <div className={styles.collectionSkeletonLine} />
                                    </div>
                                </div>
                            ))
                        ) : collections.map((collection) => <CollectionCard key={collection.id} collection={collection} />)}
                    </div>
                </section>
            ) : null}

            <section className={styles.supportSection}>
                <div className={styles.supportMedia}>
                    <img
                        src={fightHeroMobile}
                        srcSet={fightHeroSrcSet}
                        sizes="(min-width: 900px) 44vw, 100vw"
                        alt="Action shot from a fight"
                        className={styles.supportImage}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <div className={styles.supportContent}>
                    <span className={styles.supportTag}>Need help?</span>
                    <h2>
                        Can’t find the fight
                        <br />
                        you’re looking for?
                    </h2>
                    <p>
                        Tell us what you want to watch and our team will point you to the right event, replay,
                        or collection.
                    </p>
                    <Button
                        style={styles.primaryButton}
                        styleType={ButtonStyleType.FILL}
                        textSize={TextSize.LARGE}
                        text="Contact Us"
                        callback={() => NavigateToInternalUrl(Routes.CONTACT)}
                    />
                </div>
            </section>

            {!hasActiveSubscription ? (
                <section className={styles.ctaStrip}>
                    <div className={styles.ctaCopy}>
                        <h2>Everything you love about combat sports, all in one place.</h2>
                        <p>Join now for exclusive replays, interviews, and premium stories.</p>
                    </div>
                    <Button
                        style={styles.primaryButton}
                        styleType={ButtonStyleType.FILL}
                        textSize={TextSize.LARGE}
                        text="View Plans"
                        callback={() => NavigateToInternalUrl(Routes.PRICING)}
                    />
                </section>
            ) : null}

            <section className={styles.sectionAlt}>
                <SectionHeader
                    title="Quick Actions"
                    subtitle="Get closer to the action in just a few clicks."
                />
                <div className={styles.actionGrid}>
                    {isAdminUser ? (
                        <ActionTile
                            title="Buy Tickets"
                            description="Secure your seat for the next fight night."
                            label="Get Tickets"
                            route={Routes.CONTACT}
                        />
                    ) : null}
                    <ActionTile
                        title="Why CombatTV"
                        description="See how CombatTV brings NZ fights, highlights, interviews, and local history together."
                        label="Learn More"
                        route={Routes.NZ_COMBAT_SPORTS}
                    />
                    <ActionTile
                        title="Video Search"
                        description="Find fights, highlights, and interviews instantly."
                        label="Search Library"
                        route={Routes.VIDEOS}
                    />
                    <ActionTile
                        title="Share Combat TV"
                        description="Invite friends to watch the next fight night."
                        label="Share"
                        onAction={handleShare}
                    />
                </div>
            </section>

            {shouldReserveFightCardSection ? (
                <section className={styles.section}>
                    <SectionHeader
                        title="Fight Cards"
                        subtitle="Preview upcoming events and the full fight lineups."
                    />
                    <div className={styles.fightCardGrid}>
                        {fightCardStatus === 'loading' ? (
                            skeletonItems.map((item) => (
                                <div key={item} className={styles.fightCardSkeleton} aria-hidden="true">
                                    <div className={styles.fightCardSkeletonMedia} />
                                    <div className={styles.fightCardSkeletonBody}>
                                        <div className={styles.fightCardSkeletonLine} />
                                        <div className={styles.fightCardSkeletonLineShort} />
                                        <div className={styles.fightCardSkeletonLine} />
                                    </div>
                                </div>
                            ))
                        ) : fightCards.map((event) => (
                            <a
                                key={event.id}
                                className={styles.fightCard}
                                href={`${Routes.FIGHT_CARD_EVENT}/${event.id}`}
                                onClick={(clickEvent) => handleFightCardBrowse(clickEvent, event.id)}
                                aria-label={`View fight card for ${event.eventName}`}
                            >
                                <div className={styles.fightCardMedia}>
                                    {event.thumbnailUrl ? (
                                        <img src={event.thumbnailUrl} alt={event.eventName} />
                                    ) : (
                                        <div className={styles.fightCardMediaPlaceholder} />
                                    )}
                                </div>
                                <div className={styles.fightCardHeader}>
                                    <div>
                                        <h3>{event.eventName}</h3>
                                        <p>{event.location}</p>
                                    </div>
                                    <span className={styles.fightCardDate}>{event.dateLabel}</span>
                                </div>
                                <ul className={styles.fightList}>
                                    {event.fights.map((fight, index) => (
                                        <li key={`${fight}-${index}`}>{fight}</li>
                                    ))}
                                </ul>
                                <span className={styles.fightCardCta}>View card</span>
                            </a>
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
};

export default HomeModern;
